import {
    faChevronDown,
    faChevronRight,
    faFloppyDisk,
    faRotate,
    faServer,
    faTrash,
    faUser,
    faWrench,
    faPowerOff,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useState } from 'react'
import DateTimeFormated from '~/components/DateTimeFormated'
import { Block } from '~/team_tools/accounts/Block'
import { mapLabel, pickableMaps } from './maps'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { Activity, ActivityChart } from './ActivityChart'
import { CheckField, Detail, SelectField, TextField } from './Field'
import { DrainButton, DrainConfirm, useDrain } from './DrainButton'
import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { ReportingDot, ReportingRow } from './ReportingBadge'
import { PlayerRows, ago } from './PlayerRows'
import { STATUS_STYLE } from './status'
import { lootDrawLabel } from './templatesApi'
import {
    DeathFees,
    Server,
    ServerLoot,
    ServerPlayer,
    getServerDeathFees,
    getServerHealth,
    getServerLoot,
    getServerPlayers,
    setServerLootConfig,
    topUpServerLoot,
} from './api'

// Re-exported because this module used to own it and other cards import it
// from here.
export { ago }

// Which model the field belongs to is the backend's problem -- ServerUpdateView
// routes each key to the server row or its config -- so the card keeps one flat
// draft and sends whatever changed in a single call.
const NUMERIC = ['fee', 'max_players', 'min_sessions', 'max_sessions', 'min_kills', 'max_kills']

export const ServerCard = ({
    server,
    maps,
    canEdit,
    // Restart, stop, rebuild, destroy and drain. Defaults on so the older
    // screens that render this card keep behaving as they did.
    canOperate = true,
    // Set by the fleet board's sheet, which already polls /health.
    sharedHealth,
    expanded,
    onToggle,
    onSave,
    onRestart,
    onHardRestart,
    onStop,
    onDestroy,
    // 'sheet' is the card inside the fleet board's popup: the board's own
    // header already names the server and says how it is, so the card keeps
    // its controls and its body and drops the duplicate title line.
    variant = 'card',
}: any) => {
    const s: Server = server
    const sheet = variant === 'sheet'
    const destroyed = s.status === 'destroyed'
    const editable = canEdit && !destroyed

    // Edits accumulate here until Save. Committing on blur meant a half-typed
    // value could be written by clicking elsewhere, and gave no single place to
    // see what was about to change.
    const [draft, setDraft] = useState<Record<string, any>>({})
    const [saving, setSaving] = useState(false)
    const [justSaved, setJustSaved] = useState(false)
    const [busy, setBusy] = useState(false)
    const drain = useDrain()

    // A refresh that lands while a field is open would otherwise silently swap
    // the value under the cursor; dropping the draft only on identity change
    // keeps edits alive across polls of the same server.
    useEffect(() => {
        setDraft({})
    }, [s.id])

    const val = (field: string, current: any) => (field in draft ? draft[field] : current)
    const set = (field: string) => (v: any) => setDraft((d) => ({ ...d, [field]: v }))
    const dirty = Object.keys(draft).length > 0

    const save = async () => {
        setSaving(true)
        const payload = Object.fromEntries(
            Object.entries(draft).map(([k, v]) => [k, NUMERIC.includes(k) && v === '' ? null : v])
        )
        try {
            await onSave(payload)
            setDraft({})
            setJustSaved(true)
            setTimeout(() => setJustSaved(false), 1500)
        } finally {
            setSaving(false)
        }
    }

    const runAction = async (fn: () => Promise<any>) => {
        setBusy(true)
        try {
            await fn()
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className='flex flex-col gap-2 p-2 bg-black bg-opacity-20 rounded border-gray-500 border-opacity-50'>
            {/* flex-nowrap + min-w-0 + truncate: the summary shortens instead of
                wrapping the header onto a second line. */}
            <div
                className={`flex flex-row flex-nowrap items-center gap-2 border-b border-white border-opacity-20 pb-1 ${
                    sheet ? 'justify-end' : 'cursor-pointer'
                }`}
                onClick={sheet ? undefined : onToggle}
            >
                {!sheet && (
                    <>
                        <FontAwesomeIcon
                            icon={expanded ? faChevronDown : faChevronRight}
                            className='opacity-40 w-3 shrink-0'
                        />
                        <FontAwesomeIcon icon={faServer} className='shrink-0' />
                        <span className={`font-bold whitespace-nowrap ${destroyed ? 'opacity-40 line-through' : ''}`}>
                            {s.name ?? <span className='opacity-50 font-normal italic'>unconfigured</span>}
                        </span>
                        {/* Which kill race this server counts toward, or that it
                            counts toward none. */}
                        {!destroyed &&
                            ((s.kill_races ?? []).length ? (
                                <span
                                    className='whitespace-nowrap shrink-0 text-xs font-bold text-yellow-400'
                                    title={(s.kill_races ?? [])
                                        .map((r: any) => `${r.name} — ${r.running ? 'live' : 'starts ' + new Date(r.begins_at).toLocaleString()}`)
                                        .join('\n')}
                                >
                                    {Array.from(new Set((s.kill_races ?? []).map((r: any) => r.name))).join(', ')}
                                </span>
                            ) : (
                                <span className='whitespace-nowrap shrink-0 text-xs font-bold uppercase text-red-500'>
                                    no kill race
                                </span>
                            ))}
                        <small className='text-white/50 whitespace-nowrap shrink-0'>{s.slot ?? `id: ${s.id}`}</small>
                        <span
                            className={`text-xs uppercase tracking-wide whitespace-nowrap shrink-0 ${STATUS_STYLE[s.diagnosis] ?? ''}`}
                            title={s.diagnosis_reasons?.join('\n') || s.diagnosis}
                        >
                            {s.diagnosis}
                        </span>
                        <ReportingDot reporting={s.reporting} ageSeconds={s.roster_age_seconds} />
                        <span className='flex flex-row items-center gap-1 text-xs text-white/60 whitespace-nowrap shrink-0'>
                            <FontAwesomeIcon icon={faUser} />
                            {s.players_count}
                        </span>
                        <small className='text-white/40 uppercase tracking-wide truncate min-w-0 flex-1'>
                            {s.region} · {s.status} · {s.fee ?? 'free'} · {mapLabel(s.map_name)}
                        </small>
                    </>
                )}

                {!destroyed && (
                    <div className='flex flex-row gap-2 shrink-0' onClick={(e) => e.stopPropagation()}>
                        {/* Drain first: it is the gentle one, and the one to
                            reach for before any of the others. */}
                        {s.status === 'active' && (
                            <ActionButton
                                onClick={() => runAction(() => drain(s.id, s.name || s.slot || s.host))}
                                disabled={busy || !canOperate}
                                title='Drain: stop taking joins and exit once the matches on it finish'
                                className='text-green-400'
                            >
                                <FontAwesomeIcon icon={faArrowRightFromBracket} />
                            </ActionButton>
                        )}
                        <ActionButton
                            onClick={(e) => runAction(() => onRestart(e.shiftKey))}
                            disabled={!s.slot || busy || !canOperate}
                            title={
                                s.slot
                                    ? 'Restart the game container now. Shift+click to also refund every spawn fee on it'
                                    : 'No slot id to address'
                            }
                            className='text-blue-400'
                        >
                            <FontAwesomeIcon icon={faRotate} />
                        </ActionButton>
                        {s.status === 'active' && (
                            <ActionButton
                                onClick={(e) => runAction(() => onStop(e.shiftKey))}
                                disabled={busy || !canOperate}
                                title='Stop taking players now; the machine stays up. Shift+click to also refund every spawn fee on it'
                                className='text-white/70'
                            >
                                <FontAwesomeIcon icon={faPowerOff} />
                            </ActionButton>
                        )}
                        <ActionButton
                            onClick={(e) => runAction(() => onHardRestart(e.shiftKey))}
                            disabled={!s.slot || busy || !canOperate}
                            title={
                                s.slot
                                    ? 'Rebuild now: destroy the machine and build a fresh one in the same slot. Shift+click to also refund every spawn fee on it'
                                    : 'No slot id to address'
                            }
                            className='text-amber-400'
                        >
                            <FontAwesomeIcon icon={faWrench} />
                        </ActionButton>
                        <ActionButton
                            onClick={() => runAction(() => onDestroy())}
                            disabled={busy || !canOperate}
                            className='text-red-500'
                            title='Raise a request to destroy this server. It still needs approving above'
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </ActionButton>
                    </div>
                )}
            </div>

            {expanded && (
                <div className='flex flex-col gap-2 text-sm'>
                    <HealthBlock
                        serverId={s.id}
                        serverName={s.name || s.slot || s.host}
                        expanded={expanded}
                        chart={!sheet}
                        shared={sharedHealth}
                        canOperate={canOperate}
                    />

                    <LootBlock serverId={s.id} canAdd={canOperate && s.status === 'active'} />

                    <Block header='details' align='left'>
                        <Detail label='Host' value={s.host} />
                        <Detail label='Port' value={s.port} />
                        <Detail label='Slot' value={s.slot ?? '-'} />
                        <Detail label='Region' value={s.region} />
                        <Detail label='Template' value={s.profile?.name ?? '-'} />
                        <Detail
                            label='Last heartbeat'
                            value={
                                s.last_heartbeat_at ? (
                                    <DateTimeFormated date={s.last_heartbeat_at} className='text-xs' />
                                ) : (
                                    <span className='opacity-40'>never</span>
                                )
                            }
                        />
                        <Detail label='Created' value={<DateTimeFormated date={s.created_at} className='text-xs' />} />
                        {s.destroyed_at && (
                            <Detail
                                label='Destroyed'
                                value={<DateTimeFormated date={s.destroyed_at} className='text-xs' />}
                            />
                        )}
                    </Block>

                    {/* Folded by default: the popup is opened to see how a
                        server is doing far more often than to change it, and
                        two forms of inputs between the health and the roster
                        was a lot of scrolling past. The summary keeps the
                        essentials readable while folded. */}
                    <Block
                        header='settings'
                        align='left'
                        collapsible
                        defaultOpen={false}
                        summary={`${s.fee ? `$${s.fee}` : 'free'} · ${s.max_players ?? 'no cap'} max · ${s.map_name ?? '-'}${s.password || s.password_set ? ' · password' : ''}`}
                    >
                        <TextField
                            label='Name'
                            value={val('name', s.name)}
                            onChange={set('name')}
                            placeholder='unconfigured'
                            disabled={!editable}
                        />
                        <CheckField
                            label='Active'
                            checked={val('status', s.status) === 'active'}
                            onChange={(on) => set('status')(on ? 'active' : 'inactive')}
                            disabled={!editable}
                            hint='listed in the game client and accepting joins'
                        />
                        <TextField
                            label='Spawn fee'
                            value={val('fee', s.fee)}
                            onChange={set('fee')}
                            placeholder='free'
                            disabled={!editable}
                        />
                        {/* Who last re-priced this server. Every fee change is
                            logged (gameapi FeeChange); the full history is in
                            the admin under "Fee changes". */}
                        {s.last_fee_change && (
                            <Detail
                                label='Fee last changed'
                                value={
                                    <span className='text-xs'>
                                        {s.last_fee_change.old_fee ?? 'free'} → {s.last_fee_change.new_fee ?? 'free'}
                                        {' by '}
                                        {s.last_fee_change.by ?? 'no signed-in user'}
                                        {', '}
                                        <DateTimeFormated date={s.last_fee_change.at} className='text-xs' />
                                    </span>
                                }
                            />
                        )}
                        <TextField
                            label='Max players'
                            value={val('max_players', s.max_players)}
                            onChange={set('max_players')}
                            placeholder='no cap'
                            disabled={!editable}
                        />
                        <SelectField
                            label='Map'
                            value={val('map_id', s.map_id)}
                            onChange={set('map_id')}
                            disabled={!editable}
                            options={pickableMaps(maps as any[]).map((m: any) => ({
                                value: m.id,
                                label: mapLabel(m.name),
                            }))}
                        />
                        <TextField
                            label='Password'
                            value={val('password', s.password)}
                            onChange={set('password')}
                            // Only editors are sent the password itself, so a
                            // set one reads as hidden rather than as "public".
                            placeholder={!s.password && s.password_set ? 'set (hidden)' : 'public'}
                            disabled={!editable}
                        />
                    </Block>

                    <Block
                        header='player requirements'
                        align='left'
                        collapsible
                        defaultOpen={false}
                        summary={s.config?.check_requirements ? 'enforced' : 'not enforced'}
                    >
                        <CheckField
                            label='Enforced'
                            checked={val('check_requirements', s.config?.check_requirements)}
                            onChange={set('check_requirements')}
                            disabled={!editable}
                            hint='reject joins that fall outside the limits below'
                        />
                        <TextField
                            label='Min sessions'
                            value={val('min_sessions', s.config?.min_sessions)}
                            onChange={set('min_sessions')}
                            placeholder='any'
                            disabled={!editable}
                        />
                        <TextField
                            label='Max sessions'
                            value={val('max_sessions', s.config?.max_sessions)}
                            onChange={set('max_sessions')}
                            placeholder='any'
                            disabled={!editable}
                        />
                        <TextField
                            label='Min kills'
                            value={val('min_kills', s.config?.min_kills)}
                            onChange={set('min_kills')}
                            placeholder='any'
                            disabled={!editable}
                        />
                        <TextField
                            label='Max kills'
                            value={val('max_kills', s.config?.max_kills)}
                            onChange={set('max_kills')}
                            placeholder='any'
                            disabled={!editable}
                        />
                    </Block>

                    <PlayersPanel serverId={s.id} expanded={expanded} />

                    {editable && (
                        <div className='flex flex-row items-center justify-end gap-3'>
                            {dirty && (
                                <span className='text-xs uppercase tracking-wide opacity-50'>
                                    {Object.keys(draft).length} unsaved change
                                    {Object.keys(draft).length === 1 ? '' : 's'}
                                </span>
                            )}
                            {justSaved && <span className='text-xs uppercase tracking-wide text-green-500'>saved</span>}
                            <button
                                onClick={save}
                                disabled={!dirty || saving}
                                className='rounded bg-violet-600/80 hover:bg-violet-600 disabled:opacity-30 disabled:hover:bg-violet-600/80 px-4 py-1 text-xs font-bold uppercase tracking-wide'
                            >
                                <FontAwesomeIcon icon={faFloppyDisk} className='mr-1' />
                                {saving ? 'saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

const HealthBlock = ({
    serverId,
    serverName,
    expanded,
    chart = true,
    shared,
    canOperate = true,
}: {
    serverId: number
    serverName: string
    expanded: boolean
    /** Off when a bigger chart of the same series sits above this block. */
    chart?: boolean
    /** Health the parent already polls; when given, this block does not poll. */
    shared?: { health: any; reload: () => void }
    /** Drain is DeveloperPermission, like the card's other operations. */
    canOperate?: boolean
}) => {
    const [own, setHealth] = useState<any>(null)
    const [draining, setDraining] = useState(false)
    const health = shared ? shared.health : own

    // Named so an action can pull the diagnosis again immediately
    // rather than leaving a stale count on screen for up to 30 seconds.
    const reloadOwn = useCallback(async () => {
        try {
            setHealth(await getServerHealth(serverId))
        } catch {
            setHealth(null)
        }
    }, [serverId])
    const reload = shared ? shared.reload : reloadOwn

    useEffect(() => {
        if (!expanded || shared) return
        let alive = true
        const load = async () => {
            try {
                const data = await getServerHealth(serverId)
                if (alive) setHealth(data)
            } catch {
                if (alive) setHealth(null)
            }
        }
        load()
        const timer = setInterval(load, 30000)
        return () => {
            alive = false
            clearInterval(timer)
        }
    }, [serverId, expanded, Boolean(shared)])

    if (!health) {
        return (
            <Block header='health' align='left'>
                <div className='col-span-2 text-xs opacity-30 py-2'>checking…</div>
            </Block>
        )
    }

    const sig = health.signals
    const activity: Activity = health.activity

    return (
        <Block header='health' align='left'>
            <div className='col-span-2 flex flex-col gap-1'>
                <span className={`font-bold uppercase text-xs ${STATUS_STYLE[health.status] ?? ''}`}>
                    {health.status}
                    {/* D-095: the age beside the status, so six quiet hours read as such. */}
                    <span className='ml-2 font-normal normal-case opacity-60'>
                        {sig.heartbeat_age_seconds === null || sig.heartbeat_age_seconds === undefined
                            ? 'never reported'
                            : `last report ${ago(sig.heartbeat_age_seconds)}`}
                    </span>
                </span>
                {health.reasons.map((r: string, i: number) => (
                    <span key={i} className='text-xs opacity-70'>
                        · {r}
                    </span>
                ))}
            </div>

            <Detail
                label='Players'
                value={
                    <span className={sig.reporting ? '' : 'text-amber-400'}>
                        {sig.online}
                        {sig.max_players ? ` / ${sig.max_players}` : ''}
                    </span>
                }
            />
            <Detail label='In a session' value={sig.playing} />
            {/* The heartbeat's age, not the last roster or result. */}
            <Detail label='Last heartbeat' value={ago(sig.heartbeat_age_seconds)} />
            <Detail label='Sessions (30m)' value={sig.recent_sessions_30m} />
            <Detail label='Kills (30m)' value={sig.recent_kills_30m} />
            <DeathFeeRows serverId={serverId} />

            <div className='col-span-2 min-w-0 flex flex-col gap-2'>
                <ReportingRow reporting={sig.reporting} ageSeconds={sig.roster_age_seconds} />
                {draining ? (
                    <DrainConfirm
                        serverId={serverId}
                        serverName={serverName}
                        online={sig.online}
                        reporting={sig.reporting}
                        onDone={reload}
                        onCancel={() => setDraining(false)}
                    />
                ) : (
                    <div>
                        <DrainButton onAsk={() => setDraining(true)} disabled={!canOperate} />
                    </div>
                )}
            </div>

            {chart && (
                <div className='col-span-2'>
                    <ActivityChart activity={activity} />
                </div>
            )}
        </Block>
    )
}

const PlayersPanel = ({ serverId, expanded }: { serverId: number; expanded: boolean }) => {
    const [players, setPlayers] = useState<ServerPlayer[] | null>(null)
    const [asOf, setAsOf] = useState<string | null>(null)

    // Polls only while this card is open. Every expanded card polling in the
    // background would multiply the load by however many rows are open.
    useEffect(() => {
        if (!expanded) return
        let alive = true
        const load = async () => {
            try {
                const data = await getServerPlayers(serverId)
                if (!alive) return
                setPlayers(data.players)
                setAsOf(data.as_of)
            } catch {
                if (alive) setPlayers([])
            }
        }
        load()
        const timer = setInterval(load, 15000)
        return () => {
            alive = false
            clearInterval(timer)
        }
    }, [serverId, expanded])

    return (
        <div className='flex flex-col gap-2 bg-gray-500 bg-opacity-10 rounded p-2 w-full'>
            <div className='text-xs uppercase tracking-widest opacity-50 text-left'>
                players online{players ? ` (${players.length})` : ''}
            </div>

            {players === null && <div className='text-xs opacity-30 py-2'>loading…</div>}

            {players?.length === 0 && (
                <div className='text-xs opacity-30 py-2'>
                    Nobody here. Presence expires with the online timeout, so a crashed server empties out on its own.
                </div>
            )}

            {!!players?.length && <PlayerRows players={players} serverId={serverId} />}

            {asOf && (
                <div className='text-[10px] opacity-30 text-left'>
                    as of <DateTimeFormated date={asOf} /> · refreshes every 15s while open
                </div>
            )}
        </div>
    )
}

/** Loot on this server, live while the card is open. One box per currency --
 *  the pool, what a drop pays, what is on the map, what was given away, with
 *  add-funds and drop-size controls -- and the last pickups in their own box.
 *  Read on open and every 15 seconds. */
const LootBlock = ({ serverId, canAdd }: { serverId: number; canAdd: boolean }) => {
    const [loot, setLoot] = useState<ServerLoot | null>(null)
    const [failed, setFailed] = useState(false)
    const [tick, setTick] = useState(0)

    useEffect(() => {
        let live = true
        const read = () =>
            getServerLoot(serverId)
                .then((data) => {
                    if (!live) return
                    setLoot(data)
                    setFailed(false)
                })
                .catch(() => live && setFailed(true))
        read()
        const timer = setInterval(read, 15000)
        return () => {
            live = false
            clearInterval(timer)
        }
    }, [serverId, tick])

    const refresh = () => setTick((n) => n + 1)
    const currencies = Array.from(
        new Set([...(loot?.pools ?? []).map((p) => p.currency), ...(canAdd ? ['USDC', 'POINT'] : [])])
    )

    return (
        // Everything about loot in one box of its own, apart from the
        // server's health and details.
        <Block
            header='Loot'
            align='left'
            collapsible
            defaultOpen
            summary={(loot?.pools ?? [])
                .map((p) => `${lootLabel(p.currency)} ${lootAmount(p.currency, p.balance)} left`)
                .join(' · ')}
        >
        <div className='col-span-full flex flex-col gap-2'>
            {loot && (
                <LootCadence
                    serverId={serverId}
                    frequency={loot.update_frequency}
                    drops={loot.max_spawn_points}
                    canEdit={canAdd}
                    onChanged={refresh}
                />
            )}
            {failed && <span className='text-xs text-red-400'>Loot could not be read.</span>}
            {!loot && !failed && <span className='text-xs opacity-50'>Loading loot…</span>}
            {loot &&
                currencies.map((currency) => (
                    <LootPoolBox
                        key={currency}
                        serverId={serverId}
                        currency={currency}
                        pool={loot.pools.find((p) => p.currency === currency)}
                        onMap={loot.on_map.find((m) => m.currency === currency)}
                        given={loot.given.find((g) => g.currency === currency)}
                        canAdd={canAdd}
                        onChanged={refresh}
                    />
                ))}
            {loot && loot.recent.length > 0 && (
                <Block
                    header={`Last pickups — ${loot.recent.length}`}
                    align='left'
                    collapsible
                    defaultOpen
                >
                    {/* Scrolls past eight rows instead of growing the card. */}
                    <div className='col-span-full flex max-h-44 flex-col gap-0.5 overflow-y-auto pr-1 text-xs'>
                        {loot.recent.map((r, i) => (
                            <span key={i} className='flex flex-row gap-2'>
                                <span className='w-40 shrink-0 whitespace-nowrap text-white/40'>
                                    {new Date(r.at).toLocaleString([], {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                    })}
                                </span>
                                <span className='min-w-0 flex-1 truncate'>{r.username ?? '—'}</span>
                                <span className='tabular-nums'>{lootAmount(r.currency, r.amount)}</span>
                            </span>
                        ))}
                    </div>
                </Block>
            )}
        </div>
        </Block>
    )
}

const lootLabel = (c: string) => (c === 'POINT' ? 'Points' : c)
const lootAmount = (c: string, v: string | number) =>
    c === 'POINT' ? `${Number(v).toFixed(0)} pts` : `$${Number(v).toFixed(2)}`
/** One click adds for the common amounts; anything else goes in the box. */
const QUICK_ADD: Record<string, string[]> = { USDC: ['1', '5', '10'], POINT: ['100', '500', '1000'] }

const LootPoolBox = ({
    serverId,
    currency,
    pool,
    onMap,
    given,
    canAdd,
    onChanged,
}: {
    serverId: number
    currency: string
    pool?: ServerLoot['pools'][number]
    onMap?: ServerLoot['on_map'][number]
    given?: ServerLoot['given'][number]
    canAdd: boolean
    onChanged: () => void
}) => {
    const [custom, setCustom] = useState('')
    const [low, setLow] = useState(pool?.reward_min ?? '')
    const [high, setHigh] = useState(pool?.reward_max ?? '')
    const [busy, setBusy] = useState(false)
    const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
    // Follow the server's drop size until somebody starts editing it.
    const [editingSize, setEditingSize] = useState(false)
    useEffect(() => {
        if (editingSize) return
        setLow(pool?.reward_min ?? '')
        setHigh(pool?.reward_max ?? '')
    }, [pool?.reward_min, pool?.reward_max, editingSize])

    const send = async (body: { amount?: string; reward_min?: string; reward_max?: string }, done: string) => {
        setBusy(true)
        setNote(null)
        try {
            const result = await topUpServerLoot(serverId, { currency, ...body })
            setNote({ ok: true, text: `${done} Pool now ${lootAmount(currency, result.balance)}.` })
            setEditingSize(false)
            setCustom('')
            onChanged()
        } catch (err: any) {
            setNote({ ok: false, text: err?.message ?? 'That did not save.' })
        } finally {
            setBusy(false)
        }
    }

    const isNew = !pool
    const input = 'w-16 rounded border border-white/20 bg-transparent px-1.5 py-0.5 text-right tabular-nums'
    const chip =
        'rounded border border-amber-400/50 px-2 py-0.5 tabular-nums text-amber-300 hover:border-amber-300 disabled:opacity-30'
    const summary = pool
        ? `${onMap?.count ?? 0} on map · ${lootAmount(currency, given?.all_time ?? 0)} given · ${lootAmount(currency, pool.balance)} left`
        : 'no pool'

    return (
        <Block
            header={`${lootLabel(currency)} loot${pool ? ` — ${lootAmount(currency, pool.balance)} left` : ''}`}
            align='left'
            collapsible
            defaultOpen={!isNew}
            summary={summary}
        >
            <div className='col-span-full flex flex-col gap-2 text-xs'>
                {pool ? (
                    <div className='grid grid-cols-3 gap-x-4 gap-y-1'>
                        <LootStat
                            label='On the map now'
                            value={
                                onMap?.count
                                    ? `${onMap.count} drop${onMap.count === 1 ? '' : 's'} · ${lootAmount(currency, onMap.total)}`
                                    : 'none'
                            }
                            tone={onMap?.count ? 'text-green-400' : undefined}
                        />
                        <LootStat
                            label='Given away'
                            value={lootAmount(currency, given?.all_time ?? 0)}
                            tone='text-amber-300'
                        />
                        <LootStat label='Left' value={lootAmount(currency, pool.balance)} />
                    </div>
                ) : (
                    <span className='text-white/40'>No {lootLabel(currency)} pool on this server yet.</span>
                )}

                {canAdd && (
                    <>
                        <div className='flex flex-row flex-wrap items-center gap-1.5'>
                            <span className='w-20 text-[10px] uppercase tracking-widest text-white/40'>add funds</span>
                            {!isNew &&
                                QUICK_ADD[currency].map((value) => (
                                    <button
                                        key={value}
                                        type='button'
                                        disabled={busy}
                                        onClick={() => send({ amount: value }, `Added ${lootAmount(currency, value)}.`)}
                                        className={chip}
                                    >
                                        +{lootAmount(currency, value)}
                                    </button>
                                ))}
                            <input
                                inputMode='decimal'
                                value={custom}
                                onChange={(e) => setCustom(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && custom && !isNew)
                                        send({ amount: custom }, `Added ${lootAmount(currency, custom)}.`)
                                }}
                                placeholder={currency === 'POINT' ? '250' : '2.50'}
                                className={input}
                            />
                            {!isNew && (
                                <button
                                    type='button'
                                    disabled={busy || !custom}
                                    onClick={() => send({ amount: custom }, `Added ${lootAmount(currency, custom)}.`)}
                                    className={chip}
                                >
                                    add
                                </button>
                            )}
                        </div>
                        <div className='flex flex-row flex-wrap items-center gap-1.5'>
                            <span className='w-20 text-[10px] uppercase tracking-widest text-white/40'>each drop</span>
                            <input
                                inputMode='decimal'
                                value={low}
                                onChange={(e) => {
                                    setEditingSize(true)
                                    setLow(e.target.value)
                                }}
                                placeholder='min'
                                className={input}
                            />
                            <span className='text-white/40'>to</span>
                            <input
                                inputMode='decimal'
                                value={high}
                                onChange={(e) => {
                                    setEditingSize(true)
                                    setHigh(e.target.value)
                                }}
                                placeholder='max'
                                className={input}
                            />
                            <span className='text-white/40'>{currency === 'POINT' ? 'points' : 'USDC'}</span>
                            {low && high && (
                                <span className='w-full pl-[5.4rem] text-[10px] text-white/40'>
                                    {lootDrawLabel(currency, low, high)}, while the pool holds that much
                                </span>
                            )}
                            {isNew ? (
                                <button
                                    type='button'
                                    disabled={busy || !custom || !low || !high}
                                    onClick={() =>
                                        send(
                                            { amount: custom, reward_min: low, reward_max: high },
                                            `Pool made with ${lootAmount(currency, custom)}.`
                                        )
                                    }
                                    className={chip}
                                >
                                    make pool
                                </button>
                            ) : (
                                editingSize && (
                                    <button
                                        type='button'
                                        disabled={busy || !low || !high}
                                        onClick={() =>
                                            send({ reward_min: low, reward_max: high }, 'Drop size saved.')
                                        }
                                        className={chip}
                                    >
                                        save drop size
                                    </button>
                                )
                            )}
                        </div>
                    </>
                )}
                {note && <span className={note.ok ? 'text-green-400' : 'text-red-400'}>{note.text}</span>}
            </div>
        </Block>
    )
}

const LootStat = ({ label, value, tone }: { label: string; value: string; tone?: string }) => (
    <span className='flex flex-col'>
        <span className='text-[10px] uppercase tracking-widest text-white/40'>{label}</span>
        <span className={`text-sm font-bold tabular-nums ${tone ?? 'text-white'}`}>{value}</span>
    </span>
)

/** What BR1 kept off players' deaths here -- the cut of each kill payout --
 *  in USDC and airdrop credit, by how the killed player paid to spawn. Not
 *  spawn fees. Refreshed every 30 seconds while the card is open. */
const DeathFeeRows = ({ serverId }: { serverId: number }) => {
    const [fees, setFees] = useState<DeathFees | null>(null)
    useEffect(() => {
        let live = true
        const read = () =>
            getServerDeathFees(serverId)
                .then((data) => live && setFees(data))
                .catch(() => live && setFees(null))
        read()
        const timer = setInterval(read, 30000)
        return () => {
            live = false
            clearInterval(timer)
        }
    }, [serverId])
    const show = (w?: { usdc: string; airdrop: string }) =>
        w ? (
            <span className='tabular-nums'>
                <span className='text-green-400'>${Number(w.usdc).toFixed(2)}</span>
                <span className='text-white/40'> USDC · </span>
                <span className='text-amber-300'>${Number(w.airdrop).toFixed(2)}</span>
                <span className='text-white/40'> airdrop</span>
            </span>
        ) : (
            '—'
        )
    return (
        <>
            <Detail label='Death fees (30m)' value={show(fees?.last_30m)} />
            <Detail label='Death fees (24h)' value={show(fees?.last_24h)} />
        </>
    )
}

/** How often the game asks for drops and how many it places each round. One
 *  setting for the server, not per currency. */
const LootCadence = ({
    serverId,
    frequency,
    drops,
    canEdit,
    onChanged,
}: {
    serverId: number
    frequency: number
    drops: number
    canEdit: boolean
    onChanged: () => void
}) => {
    const [seconds, setSeconds] = useState(String(frequency))
    const [perRound, setPerRound] = useState(String(drops))
    const [editing, setEditing] = useState(false)
    const [busy, setBusy] = useState(false)
    const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
    useEffect(() => {
        if (editing) return
        setSeconds(String(frequency))
        setPerRound(String(drops))
    }, [frequency, drops, editing])

    const save = async () => {
        setBusy(true)
        setNote(null)
        try {
            await setServerLootConfig(serverId, {
                update_frequency: Number(seconds),
                max_spawn_points: Number(perRound),
            })
            setNote({ ok: true, text: 'Saved. The game picks it up on its next round.' })
            setEditing(false)
            onChanged()
        } catch (err: any) {
            setNote({ ok: false, text: err?.message ?? 'That did not save.' })
        } finally {
            setBusy(false)
        }
    }

    const input = 'w-16 rounded border border-white/20 bg-transparent px-1.5 py-0.5 text-right tabular-nums'
    return (
        <div className='flex flex-row flex-wrap items-center gap-1.5 text-xs'>
            <span className='w-20 text-[10px] uppercase tracking-widest text-white/40'>frequency</span>
            <span className='text-white/50'>drops every</span>
            <input
                inputMode='numeric'
                value={seconds}
                disabled={!canEdit}
                onChange={(e) => {
                    setEditing(true)
                    setSeconds(e.target.value)
                }}
                className={input}
            />
            <span className='text-white/50'>seconds, up to</span>
            <input
                inputMode='numeric'
                value={perRound}
                disabled={!canEdit}
                onChange={(e) => {
                    setEditing(true)
                    setPerRound(e.target.value)
                }}
                className={input}
            />
            <span className='text-white/50'>spots per round</span>
            {editing && canEdit && (
                <button
                    type='button'
                    disabled={busy || !seconds || perRound === ''}
                    onClick={save}
                    className='rounded border border-amber-400/50 px-2 py-0.5 text-amber-300 hover:border-amber-300 disabled:opacity-30'
                >
                    {busy ? 'saving…' : 'save'}
                </button>
            )}
            {note && <span className={note.ok ? 'text-green-400' : 'text-red-400'}>{note.text}</span>}
        </div>
    )
}
